/**
 * PortfolioPage — certificação de trabalhos do portfólio dos workers.
 * O admin confirma que o trabalho foi feito via Dozy e que os detalhes
 * estão corretos antes de certificar (só certificados aparecem aos clientes).
 */
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import '../styles/PortfolioPage.css';
import { getPortfolioItems, certifyPortfolioItem, rejectPortfolioItem, fileUrl } from '../utils/api';
import { Award, BadgeCheck, BadgeX, Briefcase, Calendar, Images, ShieldCheck } from 'lucide-react';

const TABS = [
  { id: 'pending',   label: 'Pendentes' },
  { id: 'certified', label: 'Certificados' },
  { id: 'rejected',  label: 'Rejeitados' },
  { id: 'all',       label: 'Todos' },
];

const badge = (status) => {
  const map = {
    pending:   ['bg-yellow-500/20 text-yellow-400', 'Pendente'],
    certified: ['bg-green-500/20 text-green-400', 'Certificado'],
    rejected:  ['bg-red-500/20 text-red-400', 'Rejeitado'],
  };
  const [cls, label] = map[status] || map.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
};

const PortfolioPage = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const { success, error } = useToast();
  const [rejectModal, setRejectModal] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: items, isLoading, execute: refresh } = useApi(
    () => getPortfolioItems(activeTab),
    { immediate: true }
  );

  const changeTab = (tab) => { setActiveTab(tab); setTimeout(refresh, 0); };

  const handleCertify = async (item) => {
    setBusy(true);
    try {
      await certifyPortfolioItem(item.item_id);
      success(`Trabalho de ${item.full_name} certificado!`);
      refresh();
    } catch { error('Erro ao certificar'); }
    finally { setBusy(false); }
  };

  const handleReject = async () => {
    if (!reason.trim()) { error('Indique o motivo'); return; }
    setBusy(true);
    try {
      await rejectPortfolioItem(rejectModal.item_id, reason.trim());
      success('Publicação rejeitada');
      setRejectModal(null); setReason('');
      refresh();
    } catch { error('Erro ao rejeitar'); }
    finally { setBusy(false); }
  };

  const list = items || [];

  return (
    <div className="portfolio-admin-page space-y-6">
      <section className="portfolio-admin-hero">
        <div>
          <span className="portfolio-admin-eyebrow"><ShieldCheck className="w-4 h-4" /> Qualidade verificada</span>
          <h1>Portfólio profissional</h1>
          <p>Confirme a autenticidade dos trabalhos antes de os apresentar aos clientes da Dozy.</p>
        </div>
        <div className="portfolio-queue-card"><Images /><span><small>{TABS.find(t => t.id === activeTab)?.label}</small><strong>{list.length}</strong><em>{list.length === 1 ? 'publicação' : 'publicações'} nesta lista</em></span></div>
      </section>

      <div className="portfolio-admin-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => changeTab(tab.id)}
            className={activeTab === tab.id ? 'is-active' : ''}>
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
      ) : list.length === 0 ? (
        <div className="portfolio-empty">
          <Award className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Nenhuma publicação encontrada.</p>
        </div>
      ) : (
        <div className="portfolio-grid">
          {list.map(item => (
            <article key={item.item_id} className="portfolio-card">
              <a href={fileUrl(item.photo)} target="_blank" rel="noreferrer">
                <img src={fileUrl(item.photo)} alt={item.title}
                  className="portfolio-card__image" />
              </a>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-white font-semibold">{item.title}</p>
                  {badge(item.status)}
                </div>
                <p className="text-gray-400 text-xs mb-2">
                  por <span className="text-teal-400">{item.full_name}</span>
                  {item.profession ? ` • ${item.profession}` : ''}
                </p>
                <p className="text-gray-300 text-sm mb-3">{item.description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  {item.service_name && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{item.service_name}</span>}
                  {item.work_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(item.work_date).toLocaleDateString('pt-PT')}</span>}
                </div>
                {item.status === 'rejected' && item.reject_reason && (
                  <p className="text-red-400 text-xs mb-3">Motivo: {item.reject_reason}</p>
                )}
                {item.status === 'pending' && (
                  <div className="flex gap-2">
                    <button onClick={() => handleCertify(item)} disabled={busy}
                      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-green-500/10 text-green-400
                                 hover:bg-green-500/20 border border-green-500/20 text-sm font-medium disabled:opacity-50">
                      <BadgeCheck className="w-4 h-4" /> Certificar
                    </button>
                    <button onClick={() => setRejectModal(item)} disabled={busy}
                      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-red-500/10 text-red-400
                                 hover:bg-red-500/20 border border-red-500/20 text-sm font-medium disabled:opacity-50">
                      <BadgeX className="w-4 h-4" /> Rejeitar
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="portfolio-reject-modal max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Rejeitar Publicação</h3>
            <p className="text-gray-400 text-sm mb-4">"{rejectModal.title}" de {rejectModal.full_name} — o profissional vai ver o motivo:</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Ex: Não encontrámos registo deste trabalho na Dozy."
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm
                         focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setRejectModal(null); setReason(''); }}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 text-sm">Cancelar</button>
              <button onClick={handleReject} disabled={busy || !reason.trim()}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 text-sm disabled:opacity-50">Rejeitar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;
